/**
 * WithdrawOfferModal — buyer withdraws their own offer.
 *
 * Replaces the inline modal previously embedded in OfferManagement.js
 * (lines ~1717–1736). Uses ui/Modal for the shell.
 */
import React, { useEffect, useId, useState } from 'react';
import Modal from '../../ui/Modal';
import './WithdrawOfferModal.css';

const MAX_REASON = 500;
const ANNOUNCE_THRESHOLD = 50;

export default function WithdrawOfferModal({
  open,
  onClose,
  offer,
  submitting = false,
  onSubmit,
  error,
}) {
  const [reason, setReason] = useState('');

  const promptId = useId();
  const reasonId = useId();
  const counterId = useId();
  const errorId = useId();

  useEffect(() => {
    if (open) setReason('');
  }, [open, offer?.id]);

  const remaining = MAX_REASON - reason.length;
  const showAnnounce = remaining < ANNOUNCE_THRESHOLD;

  const handleSubmit = () => {
    if (submitting || !offer) return;
    onSubmit?.({ offer, reason });
  };

  const footer = (
    <>
      {error && (
        <p id={errorId} role="alert" className="withdraw-modal__error">
          {error}
        </p>
      )}
      <div className="withdraw-modal__actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-danger"
          onClick={handleSubmit}
          disabled={submitting || !offer}
          aria-describedby={error ? errorId : undefined}
        >
          {submitting ? 'Withdrawing…' : 'Withdraw'}
        </button>
      </div>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Withdraw offer"
      size="sm"
      describedBy={promptId}
      footer={footer}
    >
      <p id={promptId} className="withdraw-modal__prompt">
        Withdraw your offer? The seller will be notified. You can include a brief note (optional).
      </p>

      <fieldset className="withdraw-modal__fieldset">
        <legend className="withdraw-sr-only">Withdrawal reason</legend>
        <div className="withdraw-modal__field">
          <label htmlFor={reasonId} className="withdraw-modal__label">
            Reason (optional)
          </label>
          <textarea
            id={reasonId}
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
            maxLength={MAX_REASON}
            placeholder="Optional note for the seller."
            disabled={submitting}
            aria-describedby={counterId}
          />
          <div
            id={counterId}
            className="withdraw-modal__counter"
            aria-live={showAnnounce ? 'polite' : 'off'}
          >
            {showAnnounce
              ? `${remaining} characters remaining`
              : `${reason.length} / ${MAX_REASON}`}
          </div>
        </div>
      </fieldset>
    </Modal>
  );
}
